<template>
  <div class="rm-screen">
    <!-- Firma nije izabrana: izbor firme (radnici se čitaju po jednoj firmi) -->
    <template v-if="!selectedCompany">
      <p class="rm-muted rm-emp__hint">{{ $t("report_mobile.employees_pick_company") }}</p>
      <ul v-if="companies.length" class="rm-list rm-card">
        <li v-for="company in companies" :key="company.id" class="rm-emp__company">
          <button type="button" class="rm-emp__company-btn" @click="pickCompany(company)">
            <span class="rm-emp__company-name">{{ toLatin(company.name) }}</span>
            <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
          </button>
        </li>
      </ul>
      <div v-else class="rm-empty">
        <v-icon size="40" color="#8a817a">mdi-domain</v-icon>
        {{ $t("no_companies_selected") }}
      </div>
    </template>

    <div v-else-if="loading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>

    <template v-else>
      <div class="rm-stats">
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("ordered") }}</span>
          <span class="rm-stat__value rm-num">{{ stats.ordered }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("taken") }}</span>
          <span class="rm-stat__value rm-num">{{ stats.taken }}</span>
        </div>
        <div class="rm-stat rm-stat--accent">
          <span class="rm-stat__label">{{ $t("difference") }}</span>
          <span class="rm-stat__value rm-num">{{ stats.difference }}</span>
        </div>
      </div>

      <div class="rm-segment" role="tablist" :aria-label="$t('order_status')">
        <button
          v-for="item in tabs"
          :key="item.key"
          type="button"
          role="tab"
          class="rm-segment__btn"
          :class="{ 'is-active': tab === item.key }"
          :aria-selected="String(tab === item.key)"
          @click="tab = item.key"
        >
          {{ item.label }}
        </button>
      </div>

      <template v-if="shownWorkers.length">
        <ul class="rm-list rm-card">
          <li v-for="worker in shownWorkers" :key="worker.id" class="rm-emp__row">
            <button type="button" class="rm-emp__row-btn" @click="openWorker(worker)">
              <span class="rm-emp__text">
                <span class="rm-emp__name">{{ worker.name }}</span>
                <span class="rm-emp__meta rm-num">{{ worker.meta }}</span>
                <span v-if="worker.shifts.length" class="rm-emp__shifts">
                  <report-shift-tag v-for="shift in worker.shifts" :key="shift" :shift="shift"></report-shift-tag>
                </span>
              </span>
              <span class="rm-status" :class="`rm-status--${worker.status}`">
                {{ worker.statusLabel }}
              </span>
              <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
            </button>
          </li>
        </ul>
        <button v-if="hiddenCount > 0" type="button" class="rm-text-btn rm-emp__more" @click="expanded = true">
          {{ $t("report_mobile.show_more", { items: pluralT("report_mobile.workers_n", hiddenCount) }) }}
        </button>
      </template>
      <div v-else class="rm-empty">{{ emptyText }}</div>
    </template>
  </div>
</template>

<script>
import ReportMealsTabEmployees from "@/components/report/meals/tabs/ReportMealsTabEmployees.vue";
import hasWorkerList from "../../shared/hasWorkerList";
import ReportShiftTag from "../../shared/ReportShiftTag.vue";

const PAGE_SIZE = 15;

/**
 * Mobilni prikaz "Radnici": poručeno / preuzeto / razlika po radniku za jednu firmu.
 * Nasleđuje učitavanje (korisnici firme), štampu i Excel iz ReportMealsTabEmployees.
 */
export default {
  name: "ReportMobileEmployees",
  extends: ReportMealsTabEmployees,
  mixins: [hasWorkerList],
  components: { ReportShiftTag },
  data: () => ({
    // Štampa/Excel nasleđeni iz desktop taba: sve četiri smene kao kolone.
    selectedShifts: [1, 2, 3, 4],
    expanded: false,
  }),
  computed: {
    companies() {
      return this.$store.state.report.mealCompanies || [];
    },
    shownWorkers() {
      if (this.expanded || this.filters.search) {
        return this.matchedWorkers;
      }
      return this.matchedWorkers.slice(0, PAGE_SIZE);
    },
    hiddenCount() {
      return this.matchedWorkers.length - this.shownWorkers.length;
    },
    emptyText() {
      if (this.filters.search) {
        return this.$t("report_mobile.no_results_for", { search: this.filters.search });
      }
      return this.workers.length
        ? this.$t("report_mobile.no_workers_in_view")
        : this.$t("no_employee_report_data");
    },
  },
  watch: {
    selectedCompany() {
      this.expanded = false;
    },
  },
  methods: {
    pickCompany(company) {
      this.$store.commit("report/SET_SELECTED_MEAL_COMPANY", company);
    },
    openWorker(worker) {
      this.$store.commit("report/SET_SELECTED_EMPLOYEE", this.toEmployeeContext(worker));
      this.$emit("open-screen", "employee");
    },
  },
};
</script>

<style lang="scss">
.rm-emp {
  &__hint {
    padding: 0 4px;
  }

  &__company + &__company,
  &__row + &__row {
    border-top: 1px solid var(--rm-divider-soft);
  }

  &__company-btn,
  &__row-btn {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    border: none;
    background: transparent;
    text-align: left;
    color: var(--rm-text);

    &:active {
      background: var(--rm-bg);
    }
  }

  &__company-btn {
    min-height: 56px;
    padding: 10px 14px 10px 16px;
  }

  &__company-name {
    flex: 1;
    min-width: 0;
    font-size: 16px;
    font-weight: 600;
  }

  &__row-btn {
    min-height: 68px;
    padding: 10px 12px 10px 14px;
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
  }

  &__meta {
    font-size: 12px;
    color: var(--rm-muted);
  }

  &__shifts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 2px;
  }

  &__more {
    align-self: stretch;
    height: 48px;
  }
}
</style>
